import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Star, MessageSquare, Calendar, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useReviews } from '@/hooks/useReviews';
import { getProfilePictureUrl } from '@/utils/profileUtils';

interface TutorReviewsProps {
  tutorId: string;
  canAddReview?: boolean;
}

const TutorReviews = ({ tutorId, canAddReview = false }: TutorReviewsProps) => {
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [formRating, setFormRating] = useState(5);
  const [formComment, setFormComment] = useState('');
  const [formSubject, setFormSubject] = useState('');
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const { 
    reviews, 
    isLoading,
    error,
    submitReview,
    checkExistingReview,
    calculateAverageRating, 
    getRatingDistribution 
  } = useReviews(tutorId);

  const existingReview = user ? checkExistingReview(user.id) : null;

  const handleWriteReview = () => {
    if (!user) {
      // Store current URL for redirect after login
      const currentUrl = window.location.pathname + window.location.search;
      localStorage.setItem('redirectAfterAuth', currentUrl);
      navigate('/auth');
      return;
    }

    if (user.role !== 'student') {
      toast({
        title: "Access Restricted",
        description: "Only students can write reviews.",
        variant: "destructive"
      });
      return;
    }

    if (existingReview) {
      toast({
        title: "Review Already Exists",
        description: "You have already reviewed this tutor.",
        variant: "destructive"
      });
      return;
    }

    setFormRating(5);
    setFormComment('');
    setFormSubject('');
    setShowReviewForm(true);
  };

  const handleSubmitReview = async () => {
    if (!user || !formComment.trim() || !formSubject.trim()) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive"
      });
      return;
    }

    try {
      await submitReview({
        studentId: user.id,
        tutorId: tutorId,
        rating: formRating,
        comment: formComment.trim(),
        subject: formSubject.trim()
      });

      toast({
        title: "Review Submitted!",
        description: "Your review has been submitted successfully.",
      });

      setFormComment('');
      setFormSubject('');
      setFormRating(5);
      setShowReviewForm(false);
    } catch (error) {
      console.error('Error submitting review:', error);
      toast({
        title: "Error",
        description: "Failed to submit review. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleCancelForm = () => {
    setShowReviewForm(false);
    setFormComment('');
    setFormSubject('');
    setFormRating(5);
  };

  // Debug logging
  console.log('TutorReviews render - tutorId:', tutorId);
  console.log('TutorReviews render - reviews:', reviews);
  console.log('TutorReviews render - isLoading:', isLoading);
  console.log('TutorReviews render - error:', error);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Reviews</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show error state if there's an error
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2 text-red-500" />
            Reviews
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-red-600 mb-4">Error loading reviews: {error}</p>
            <Button 
              onClick={handleWriteReview}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Star className="h-4 w-4 mr-2" />
              Write First Review
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const averageRating = calculateAverageRating();
  const distribution = getRatingDistribution();

  return (
    <Card className="overflow-x-hidden">
      <CardHeader>
        <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <span>Reviews ({reviews.length})</span>
          {canAddReview && !showReviewForm && !existingReview && (
            <Button 
              onClick={handleWriteReview}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Write Review
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6 overflow-x-hidden">
        {/* Rating Summary - only show if there are reviews */}
        {reviews.length > 0 && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 p-4 bg-gray-50 rounded-lg overflow-x-hidden">
            <div className="text-center shrink-0">
              <div className="text-3xl font-bold text-gray-900">{averageRating}</div>
              <div className="flex items-center justify-center">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className={`h-4 w-4 ${i < Math.round(Number(averageRating)) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                  />
                ))}
              </div>
              <div className="text-sm text-gray-600">{reviews.length} reviews</div>
            </div>
            
            <div className="flex-1 space-y-2 min-w-0 w-full sm:w-auto">
              {[5, 4, 3, 2, 1].map(rating => (
                <div key={rating} className="flex items-center gap-2 min-w-0">
                  <span className="text-sm w-3 shrink-0">{rating}</span>
                  <Star className="h-3 w-3 text-yellow-400 fill-current shrink-0" />
                  <div className="flex-1 bg-gray-200 rounded-full h-2 min-w-0">
                    <div 
                      className="bg-yellow-400 h-2 rounded-full" 
                      style={{ width: `${reviews.length > 0 ? (distribution[rating as keyof typeof distribution] / reviews.length) * 100 : 0}%` }}
                    ></div>
                  </div>
                  <span className="text-sm text-gray-600 w-8 shrink-0 text-right">{distribution[rating as keyof typeof distribution]}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Review Form */}
        {showReviewForm && (
          <div className="border rounded-lg p-4 bg-blue-50 overflow-x-hidden">
            <h4 className="font-semibold mb-3">Write a Review</h4>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Rating</label>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map(rating => (
                    <button
                      key={rating}
                      onClick={() => setFormRating(rating)}
                      className="focus:outline-none"
                    >
                      <Star 
                        className={`h-6 w-6 cursor-pointer ${rating <= formRating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Subject</label>
                <input
                  type="text"
                  value={formSubject}
                  onChange={(e) => setFormSubject(e.target.value)}
                  placeholder="What subject did you learn?"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Comment</label>
                <textarea
                  value={formComment}
                  onChange={(e) => setFormComment(e.target.value)}
                  placeholder="Share your experience with this tutor..."
                  rows={4}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={handleSubmitReview} className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto">
                  Submit Review
                </Button>
                <Button variant="outline" onClick={handleCancelForm} className="w-full sm:w-auto">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Reviews List */}
        <div className="space-y-4 overflow-x-hidden">
          {reviews.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="mb-4">No reviews yet. Be the first to leave a review!</p>
              <Button 
                onClick={handleWriteReview}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Star className="h-4 w-4 mr-2" />
                Write First Review
              </Button>
            </div>
          ) : (
            reviews.map((review) => {
              const studentName = review.expand?.studentId?.name || 'Anonymous Student';
              const studentAvatar = getProfilePictureUrl(review.expand?.studentId);
              const reviewDate = review.created ? new Date(review.created) : new Date();
              const isCurrentUserReview = user && review.studentId === user.id;
              
              return (
                <div key={review.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors overflow-x-hidden">
                  <div className="flex items-start gap-3 min-w-0">
                    <Avatar className="h-10 w-10 shrink-0">
                      <AvatarImage src={studentAvatar} />
                      <AvatarFallback className="bg-blue-100 text-blue-600">
                        {studentName.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
                        <div className="flex flex-wrap items-center gap-2 min-w-0">
                          <span className="font-medium break-words">{studentName}</span>
                          {isCurrentUserReview && (
                            <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700 shrink-0">
                              Your Review
                            </Badge>
                          )}
                          {review.isVerified && (
                            <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 shrink-0">
                              Verified
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500 shrink-0">
                          <Calendar className="h-3 w-3" />
                          {format(reviewDate, 'MMM dd, yyyy')}
                        </div>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i} 
                              className={`h-4 w-4 ${i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                            />
                          ))}
                        </div>
                        <Badge variant="outline" className="text-xs">{review.subject}</Badge>
                      </div>
                      
                      <p className="text-gray-700 break-words">{review.comment}</p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default TutorReviews;
